import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2, RefreshCw, Trash2, AlertTriangle } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { LinesEditor } from "@/components/accounting/LinesEditor";

const FIELDS = [
  ["supplier", "Fournisseur *", "text"], ["siret", "SIRET", "text"], ["invoice_number", "N° de facture", "text"],
  ["invoice_date", "Date de facture", "date"], ["due_date", "Échéance", "date"], ["amount_ht", "Montant HT (€)", "number"],
  ["tva", "TVA (€)", "number"], ["amount_ttc", "Montant TTC (€) *", "number"], ["account_code", "Compte de charge", "text"],
];
const PAY = ["Virement", "Prélèvement", "Carte bancaire", "Chèque", "Espèces"];

function Preview({ inv }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let href;
    api.get(`/invoices/${inv.id}/file`, { responseType: "blob" }).then((r) => { href = URL.createObjectURL(r.data); setUrl(href); }).catch(() => setUrl(false));
    return () => { if (href) URL.revokeObjectURL(href); };
  }, [inv.id]);
  if (url === null) return <div className="flex h-64 items-center justify-center text-sm text-gray-400"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Chargement de la facture…</div>;
  if (!url) return <p className="p-6 text-sm text-gray-400">Aperçu indisponible.</p>;
  return (
    <div>
      {inv.content_type === "application/pdf"
        ? <iframe title="Facture" src={url} className="h-[40vh] w-full rounded-2xl border border-gray-100 lg:h-[60vh]" data-testid="invoice-preview" />
        : <img src={url} alt="Facture" className="max-h-[40vh] w-full rounded-2xl border border-gray-100 object-contain lg:max-h-[60vh]" data-testid="invoice-preview" />}
      <a href={url} target="_blank" rel="noreferrer" data-testid="invoice-open-file" className="mt-2 inline-block text-xs font-semibold text-[#065F46] hover:underline">Ouvrir la facture dans un nouvel onglet</a>
    </div>
  );
}

export function InvoiceSheet({ inv, cats, onClose, onChanged }) {
  const [f, setF] = useState(inv);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setF((p) => { const n = { ...inv }; Object.keys(p).forEach((k) => { if (p[k] !== "" && p[k] != null && p[k] !== inv[k] && p.id === inv.id) n[k] = p[k]; }); return n; });
  }, [inv]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async (validate) => {
    setSaving(true);
    try {
      const payload = { validate };
      ["supplier", "siret", "invoice_number", "invoice_date", "due_date", "amount_ht", "tva", "amount_ttc", "account_code", "category_id", "payment_method", "paid_date", "status", "notes"].forEach((k) => { payload[k] = f[k] ?? null; });
      const { data } = await api.put(`/invoices/${inv.id}`, payload);
      toast.success(validate ? "Facture validée et ajoutée aux Dépenses" : "Facture enregistrée");
      onChanged(data); if (validate) onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };
  const act = async (path, method, msg) => {
    try { await api[method](`/invoices/${inv.id}${path}`); toast.success(msg); onChanged(); onClose(); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-3xl sm:max-w-5xl" data-testid="invoice-sheet">
        <DialogHeader><DialogTitle className="font-display text-xl">{inv.supplier || inv.original_filename}</DialogTitle></DialogHeader>
        {inv.extraction === "pending" && <p className="flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="invoice-reading"><Loader2 className="h-4 w-4 animate-spin" />Lecture de la facture en cours…</p>}
        {inv.extraction === "failed" && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700">Lecture automatique impossible : complétez les champs à la main ou relancez la lecture.</p>}
        {inv.duplicate_of && <p className="flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="invoice-duplicate"><AlertTriangle className="h-4 w-4" />Une facture avec le même fournisseur et le même numéro existe déjà.</p>}
        <div className="grid gap-5 pt-2 lg:grid-cols-2">
          <Preview inv={inv} />
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {FIELDS.map(([k, l, t]) => (
                <div key={k} className={k === "supplier" ? "col-span-2" : ""}><Label className="text-xs text-gray-500">{l}</Label>
                  <Input data-testid={`invoice-${k}`} type={t} step={t === "number" ? "0.01" : undefined} value={f[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="mt-1 rounded-xl" /></div>
              ))}
              <div><Label className="text-xs text-gray-500">Catégorie de dépense</Label>
                <Select value={f.category_id || undefined} onValueChange={(v) => { set("category_id", v); const acc = cats.find((c) => c.id === v)?.account_code; if (acc) set("account_code", acc); }}>
                  <SelectTrigger data-testid="invoice-category" className="mt-1 rounded-xl"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="col-span-2"><Label className="text-xs text-gray-500">Règlement</Label>
                <div className="mt-1 flex gap-2">
                  {[["a_regler", "À régler"], ["reglee", "Réglée"]].map(([v, l]) => (
                    <button key={v} type="button" data-testid={`invoice-status-${v}`} onClick={() => set("status", v)}
                      className={`flex-1 rounded-full border px-3 py-2 text-sm font-semibold transition-colors ${f.status === v ? "border-[#065F46] bg-[#065F46] text-white" : "border-gray-200 text-gray-600 hover:bg-emerald-50"}`}>{l}</button>
                  ))}
                </div></div>
              {f.status === "reglee" && <>
                <div><Label className="text-xs text-gray-500">Date de règlement</Label><Input data-testid="invoice-paid_date" type="date" value={f.paid_date || ""} onChange={(e) => set("paid_date", e.target.value)} className="mt-1 rounded-xl" /></div>
                <div><Label className="text-xs text-gray-500">Mode de règlement</Label>
                  <Select value={f.payment_method || "Virement"} onValueChange={(v) => set("payment_method", v)}>
                    <SelectTrigger data-testid="invoice-payment" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                    <SelectContent>{PAY.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select></div>
              </>}
              <div className="col-span-2"><Label className="text-xs text-gray-500">Notes</Label><Input data-testid="invoice-notes" value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} className="mt-1 rounded-xl" /></div>
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              <Button data-testid="invoice-validate" disabled={saving} onClick={() => save(true)} className="flex-1 rounded-full bg-[#065F46] hover:bg-[#047857]">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="mr-1 h-4 w-4" />{inv.validated ? "Mettre à jour la dépense" : "Valider et ajouter aux Dépenses"}</>}
              </Button>
              {!inv.validated && <Button data-testid="invoice-save" variant="outline" disabled={saving} onClick={() => save(false)} className="rounded-full">Enregistrer le brouillon</Button>}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button data-testid="invoice-reextract" variant="ghost" size="sm" onClick={() => act("/reextract", "post", "Nouvelle lecture lancée")} className="rounded-full text-gray-500"><RefreshCw className="mr-1 h-4 w-4" />Relancer la lecture</Button>
              <Button data-testid="invoice-delete" variant="ghost" size="sm" onClick={() => window.confirm("Supprimer cette facture et la dépense liée ?") && act("", "delete", "Facture supprimée")} className="rounded-full text-red-600 hover:bg-red-50"><Trash2 className="mr-1 h-4 w-4" />Supprimer</Button>
            </div>
          </div>
        </div>
        <LinesEditor inv={inv} onChanged={onChanged} />
      </DialogContent>
    </Dialog>
  );
}
